import { useEffect, useRef } from 'react';
import { Job } from '@core/generated/gql-types';
import { useJobApplicationStore } from '@core/jobApplication/useJobApplicationStore';
import { useDaySequenceStore } from '@core/daySequence/useDaySequenceStore';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import { useJobStore } from '@core/job/useJobStore';
import type { JobDrawerView } from '@/modules/jobs/views/jobDrawer.view';

export const useJobDrawerData = (
  view: JobDrawerView,
  job: Job | null,
  isOpen: boolean,
) => {
  const { fetchJobApplicationsForJob, getJobApplicationsForJob } =
    useJobApplicationStore();
  const { fetchAllDaySequencesByJobId } = useDaySequenceStore();
  const { fetchWorkerById } = useWorkerStore();
  const { fetchJobById } = useJobStore();
  const fetchedApplicationsForJobRef = useRef<string | null>(null);

  const jobId = job?.id;
  const assignedUserId = job?.userId;
  const shouldFetchApplications = view.query.applications.fetch;
  const shouldFetchDaySequencesOnDrawerOpen =
    view.query.daySequences.fetchWhen === 'drawerOpen';
  const shouldFetchAssignedWorker = view.query.assignedWorker.fetch;

  useEffect(() => {
    if (!isOpen) {
      fetchedApplicationsForJobRef.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchRelatedData = async () => {
      if (!jobId || !isOpen) return;

      const fetchPromises: Promise<unknown>[] = [];

      // Only fetch day sequences if not already in cache
      if (shouldFetchDaySequencesOnDrawerOpen) {
        const daySequenceStore = useDaySequenceStore.getState();
        const daySequences = daySequenceStore.getDaySequenceByJobId(jobId);
        const meta = daySequenceStore.getDaySequencesMeta(jobId);
        if (daySequences.length === 0 && !meta?.fetchedAt) {
          fetchPromises.push(fetchAllDaySequencesByJobId(jobId));
        }
      }

      // Only fetch applications if not already in cache
      if (shouldFetchApplications) {
        const applications = getJobApplicationsForJob(jobId);
        if (
          applications.length === 0 &&
          fetchedApplicationsForJobRef.current !== jobId
        ) {
          fetchedApplicationsForJobRef.current = jobId;
          fetchPromises.push(fetchJobApplicationsForJob(jobId));
        }
      }

      fetchPromises.push(fetchJobById(jobId));

      if (shouldFetchAssignedWorker && assignedUserId) {
        const existingWorker = useWorkerStore
          .getState()
          .workers.find((worker) => worker.id === assignedUserId);
        if (!existingWorker) {
          fetchPromises.push(fetchWorkerById(assignedUserId));
        }
      }

      if (fetchPromises.length === 0) {
        return;
      }

      try {
        await Promise.all(fetchPromises);
      } catch (error) {
        console.error('Error fetching related job drawer data:', error);
      }
    };

    void fetchRelatedData();
  }, [
    jobId,
    assignedUserId,
    isOpen,
    shouldFetchApplications,
    shouldFetchDaySequencesOnDrawerOpen,
    shouldFetchAssignedWorker,
    fetchAllDaySequencesByJobId,
    fetchJobApplicationsForJob,
    fetchJobById,
    fetchWorkerById,
    getJobApplicationsForJob,
  ]);
};
